// src/services/api.js
import axios from 'axios';

// Backend API URL from environment
const API_URL = process.env.REACT_APP_API_URL || 'https://api.tourkingdom.in'; // Changed to HTTPS default

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 seconds timeout
});

// Request interceptor - Add auth token to requests and handle token refresh
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        // Parse JWT token to check expiry
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        
        // If token expires in less than 10 minutes, try to refresh it
        if (tokenPayload.exp && tokenPayload.exp < (currentTime + 600)) {
          console.log('🔄 Token expiring soon, attempting refresh...');
          
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            try {
              const response = await axios.post(`${API_URL}/api/admin/auth/refresh-token`, {
                refresh_token: refreshToken
              });

              if (response.data && response.data.success) {
                const { access_token } = response.data.data;
                localStorage.setItem('access_token', access_token);
                config.headers.Authorization = `Bearer ${access_token}`;
                console.log('✅ Token refreshed successfully');
              }
            } catch (refreshError) {
              console.error('❌ Proactive token refresh failed:', refreshError);
            }
          }
        } else {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // If token parsing fails, use the token as is
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token expiration and errors
api.interceptors.response.use(
  (response) => {
    // Extract data from our backend's success_response format
    if (response.data && response.data.success) {
      // For paginated responses, preserve the meta information
      if (response.data.meta && response.data.meta.pagination) {
        return { 
          ...response, 
          data: response.data.data,
          meta: response.data.meta 
        };
      }
      // For regular success responses, just extract the data
      else if (response.data.data !== undefined) {
        return { ...response, data: response.data.data };
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - Token expired
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post(`${API_URL}/api/admin/auth/refresh-token`, {
            refresh_token: refreshToken
          });

          if (response.data && response.data.success) {
            const { access_token } = response.data.data;
            localStorage.setItem('access_token', access_token);
            
            // Retry original request with new token
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
      }

      // If refresh fails, clear tokens and redirect to login
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_data');
      
      // Redirect to login page
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Handle other errors
    if (error.response && error.response.data) {
      const errorMessage = error.response.data.message || 
                          error.response.data.detail || 
                          'An error occurred';
      return Promise.reject(new Error(errorMessage));
    }

    return Promise.reject(error);
  }
);

export default api;